import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { cn } from "@/lib/utils";
import SvgAnglesDown from "../../styles/icons/AnglesDown";
import { ImageScrollTextData } from "./types";

interface MobileImageScrollTextProps {
  data: ImageScrollTextData;
}

export default function MobileImageScrollText({
  data,
}: MobileImageScrollTextProps) {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [fullHeight, setFullHeight] = useState(0);
  const collapsedHeight = 520;

  useEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        setFullHeight(contentRef.current.scrollHeight);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [data]);

  const shouldShowReadMore = fullHeight > collapsedHeight + 1;

  const imageSection = (
    <div className={cn("w-full relative", "min-h-[400px] md:min-h-[600px]")}>
      <Image
        src={getIconUrl(data.mainImage)}
        alt="Background"
        fill
        className={cn("pointer-events-none select-none", "object-contain")}
        priority={false}
        draggable={false}
      />
    </div>
  );

  const textSection = (
    <div className="w-full">
      <div className="relative">
        <div
          className={cn(
            "overflow-hidden transition-[max-height] duration-500 ease-in-out"
          )}
          style={{ maxHeight: isTextExpanded ? fullHeight : collapsedHeight }}
        >
          <div ref={contentRef} className="flex flex-col gap-8">
            {data.scrollText.map((block, i) => (
              <div key={i} className="mb-8">
                <h2
                  className={cn(
                    "font-serif font-light text-accent-color-two mb-4",
                    "text-2xl md:text-3xl"
                  )}
                >
                  {block.item.title}
                </h2>
                <div
                  className={cn(
                    "text-white text-base font-light leading-relaxed",
                    "[&_*:is(a)]:underline",
                    "[&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6",
                    "[&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6",
                    "scrollbar-white"
                  )}
                  dangerouslySetInnerHTML={{ __html: block.item.text }}
                />
              </div>
            ))}
          </div>
        </div>

        {!isTextExpanded && shouldShowReadMore && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-accent-color-three-dark to-transparent" />
        )}
      </div>

      {shouldShowReadMore && (
        <button
          onClick={() => setIsTextExpanded((v) => !v)}
          aria-expanded={isTextExpanded}
          className={cn(
            "flex items-center gap-3 text-white text-lg font-light mt-6",
            "hover:text-secondary transition-colors duration-200"
          )}
        >
          {isTextExpanded ? "READ LESS" : "READ MORE"}
          <SvgAnglesDown
            className={cn(
              "text-secondary transition-transform duration-300",
              isTextExpanded ? "rotate-180" : ""
            )}
          />
        </button>
      )}
    </div>
  );

  const logosSection = (
    <div
      className={cn(
        "flex flex-wrap gap-6 justify-center items-center px-[40px] py-8 bg-accent-color-three-light"
      )}
    >
      {data.logos.map((logoUrl, i) => (
        <Image
          key={i}
          src={getIconUrl(logoUrl.directus_files_id)}
          alt={`logo-${i}`}
          height={60}
          width={200}
          className={cn(
            "object-contain grayscale h-[60px] w-auto invert brightness-0"
          )}
        />
      ))}
    </div>
  );

  return (
    <section>
      <div className={cn("bg-accent-color-three-dark relative", "p-6 md:p-12")}>
        <div className="w-full flex flex-col gap-12">
          {imageSection}
          {textSection}
        </div>
      </div>
      {logosSection}
    </section>
  );
}
